import { useI18n } from '@i18n';
import { Box, Container, IconButton, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ICONS } from '@/assets/icons';
import GitBookLink from '@/components/helpcenter/GitBookLink';
import Iconify from '@/components/Iconify';
import { PBreadcrumbs } from '@/components/molecules/Breadcrumbs/PBreadcrumbs';
import Notifications from '@/components/notifications/Notifications';
import { HEADER, ICON_SIZE_MAP } from '@/config';
import { PATHS } from '@/routes';
import FolderBreadcrumb from '@/sections/Drive/components/FolderContents/FolderBreadcrumb';
import { DEFAULT_SIDEBAR_WIDTH } from '@/sections/Drive/driveLayout';
import { useBreadcrumbs } from './hooks/useBreadcrumbs';
import { useCanGoForward } from './hooks/useCanGoForward';

export function TopBar() {
  const { t } = useI18n();
  const links = useBreadcrumbs();
  const navigate = useNavigate();
  const canGoForward = useCanGoForward();
  const { pathname } = useLocation();
  // Drive owns its breadcrumb: show its folder path here instead of the route-level one, so the page's
  // top bar reads as part of the Drive UI. currentFolderId comes from the URL (this bar sits above the route).
  const isDrive = pathname.startsWith(PATHS.drive.root());
  const driveFolderId = pathname.match(/\/drive\/folder\/(\d+)/)?.[1];
  const currentFolderId = driveFolderId ? parseInt(driveFolderId, 10) : null;

  return (
    <Container
      maxWidth={isDrive ? false : 'lg'}
      disableGutters={isDrive}
      sx={{
        mb: `${HEADER.HEIGHT - 44}px`,
        width: '100%',
        mt: 2,
        height: 40,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // Drive: start the breadcrumb where the content starts (just past the sidebar); else clear the hamburger.
        pl: isDrive ? { xs: 10, lg: `var(--drive-sidebar-width, ${DEFAULT_SIDEBAR_WIDTH}px)` } : { xs: 10, lg: 3 },
        pr: isDrive ? 2 : 0,
      }}
    >
      {isDrive ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1, minWidth: 0 }}>
          <Tooltip title={t('General_Back')} placement="bottom">
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <IconButton size="small" disabled={currentFolderId === null} onClick={() => navigate(-1)}>
                <Iconify icon={ICONS.CHEVRON_LEFT} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title={t('General_Forward')} placement="bottom">
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <IconButton size="small" disabled={!canGoForward} onClick={() => navigate(1)}>
                <Iconify icon={ICONS.CHEVRON_RIGHT} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />
              </IconButton>
            </Box>
          </Tooltip>
          <FolderBreadcrumb currentFolderId={currentFolderId} />
        </Box>
      ) : (
        <Box sx={{ minWidth: 0 }}>
          <PBreadcrumbs links={links} />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <GitBookLink deep />
        <Notifications />
      </Box>
    </Container>
  );
}
